import { Reducer } from 'redux';

import {
  AttrInternalType,
  AttrSlugType,
  AttributeI,
  AttributeOptionI,
} from '~src/interfaces/attributes.types';
import { PaginationI } from '~src/interfaces/pagination.interface';
import {
  AddAttribute,
  AddValue,
  DeleteAttribute,
  DeleteValue,
  EditAttribute,
  EditValue,
  GetAllAttributes,
  GetAttributes,
  GetAttributesSearch,
  NEW_VALUE_LIST,
  RESET_ATTRIBUTES,
} from '~src/modules/attributes/constants/actionTypes';
import { StoreI } from '~src/rootReducer';
import {ThunkAnyAction, ThunkSuccessAction} from '~src/services/apiMiddleware';
import { mapKeyToData } from '~src/utils';

type AttributesAction = ThunkAnyAction<{ [key: string]: AttributeOptionI }>;

export interface AttributesState {
  loading: boolean;
  loadingAdd: boolean;
  loadingEdit: boolean;
  loadingDel: boolean;
  loadingAddOpt: boolean;
  loadingEditOpt: boolean;
  loadingDelOpt: boolean;
  error: {};
  byId: { [key: string]: AttributeI };
  items: AttributeI[];
  itemsSearch: AttributeI[];
  pagination: PaginationI | null;
  paginationSearch: PaginationI | null;
}

export function getInitialAttributesState(): AttributesState {
  return {
    loading: false,
    loadingAdd: false,
    loadingEdit: false,
    loadingDel: false,
    loadingAddOpt: false,
    loadingEditOpt: false,
    loadingDelOpt: false,
    error: {},
    byId: {},
    items: [],
    itemsSearch: [],
    pagination: null,
    paginationSearch: null,
  };
}

const addAttribute = (state: AttributesState, { response: { data } }: AttributesAction) => ({
  ...state.byId,
  [data.uuid]: data,
});

const editAttribute = (state: AttributesState, { response: { data } }: AttributesAction) => ({
  ...state.byId,
  [data.uuid]: {
    ...data,
    attribute_options: state.byId[data.uuid].attribute_options,
  },
});

const deleteAttribute = (state: AttributesState, { attributeId }: AttributesAction) => {
  const stateCopy = { ...state };
  delete stateCopy.byId[attributeId];
  return stateCopy;
};

const addAttributeValue = (state: AttributesState, action: AttributesAction) => {
  if (action.response.data.error) {
    return state.byId;
  } else {
    const attributeId = Object.keys(action.attributes)[0];
    const attribute = state.byId[attributeId];
    const attributeOptions = state.byId[attributeId].attribute_options;
    const newAttributeOptions: AttributeOptionI[] = Object.values(action.response.data);
    return {
      ...state.byId,
      [attributeId]: {
        ...attribute,
        attribute_options: attributeOptions.concat(newAttributeOptions[0]),
      },
    };
  }
};

const editAttributeValue = (state: AttributesState, { response, attributeId }: AttributesAction) => {
  const attribute = state.byId[attributeId];
  const attributeOptions = state.byId[attributeId].attribute_options;
  const newAttributeOptions: AttributeOptionI[] = Object.values(response.data);

  const updatedItems = attributeOptions.map((item) => {
    if (item.uuid === newAttributeOptions[0].uuid) {
      return { ...newAttributeOptions[0] };
    }
    return item;
  });

  return {
    ...state.byId,
    [attributeId]: {
      ...attribute,
      attribute_options: updatedItems,
    },
  };
};

const removeAttributeValue = (state: AttributesState, { attributeId, valueId }: AttributesAction) => {
  const attribute = state.byId[attributeId];
  const attributeOptions = state.byId[attributeId].attribute_options;
  const updatedItems = attributeOptions.filter((item) => item.uuid !== valueId);
  return {
    ...state.byId,
    [attributeId]: {
      ...attribute,
      attribute_options: updatedItems,
    },
  };
};

const updateAttributeOptions = (state: AttributesState, { attributeId, newList }: AttributesAction) => {
  const attribute = state.byId[attributeId];
  return {
    ...state.byId,
    [attributeId]: {
      ...attribute,
      attribute_options: newList,
    },
  };
};

const getAllAndLoadMoreItems = (stateItems: AttributeI[], action: ThunkSuccessAction): AttributeI[] =>
  action.loadMore ? [...stateItems, ...action.response.data.items] : action.response.data.items;

const attributesReducer: Reducer<AttributesState, AttributesAction> = (
  state = getInitialAttributesState(),
  action,
) => {
  switch (action.type) {
    case GetAllAttributes.Request: {
      return { ...state, loading: true };
    }
    case GetAllAttributes.Success: {
      return { ...state, loading: false, byId: mapKeyToData(action.response.data, 'uuid') };
    }
    case GetAllAttributes.Failed: {
      return { ...state, loading: false, error: action.payload };
    }
    case GetAttributes.Request:
    case GetAttributesSearch.Request:
      return { ...state, loading: true };

    case GetAttributes.Success:
      return {
        ...state,
        loading: false,
        // TODO: consider to move filtered attributes to separate cell 'optionItems'
        items: getAllAndLoadMoreItems(state.items, action as ThunkSuccessAction),
        pagination: action.response.data.pagination,
      };

    case GetAttributes.Failed:
    case GetAttributesSearch.Failed:
      return { ...state, loading: false, error: action.payload };

    case GetAttributesSearch.Success:
      return {
        ...state,
        loading: false,
        itemsSearch: getAllAndLoadMoreItems(state.itemsSearch, action as ThunkSuccessAction),
        paginationSearch: action.response.data.pagination,
      };

    case AddAttribute.Request:
      return { ...state, loadingAdd: true };
    case AddAttribute.Success: {
      return { ...state, byId: addAttribute(state, action), loadingAdd: false };
    }
    case AddAttribute.Failed:
      return { ...state, loadingAdd: false };
    case EditAttribute.Request:
      return { ...state, loadingEdit: true };
    case EditAttribute.Success: {
      return { ...state, byId: editAttribute(state, action), loadingEdit: false };
    }
    case EditAttribute.Failed:
      return { ...state, loadingEdit: false };
    case DeleteAttribute.Request:
      return { ...state, loadingDel: true };
    case DeleteAttribute.Success: {
      return { ...deleteAttribute(state, action), loadingDel: false };
    }
    case DeleteAttribute.Failed:
      return { ...state, loadingDel: false };
    case AddValue.Request:
      return { ...state, loadingAddOpt: true };
    case AddValue.Success: {
      return { ...state, byId: addAttributeValue(state, action), loadingAddOpt: false };
    }
    case AddValue.Failed:
      return { ...state, loadingAddOpt: false };
    case EditValue.Request:
      return { ...state, loadingEditOpt: true };
    case EditValue.Success: {
      return { ...state, byId: editAttributeValue(state, action), loadingEditOpt: false };
    }
    case EditValue.Failed:
      return { ...state, loadingEditOpt: false };
    case DeleteValue.Request:
      return { ...state, loadingDelOpt: true };
    case DeleteValue.Success: {
      return { ...state, byId: removeAttributeValue(state, action), loadingDelOpt: false };
    }
    case DeleteValue.Failed:
      return { ...state, loadingDelOpt: false };
    case RESET_ATTRIBUTES: {
      return { ...state, byId: {}, items: [], itemsSearch: [] };
    }
    case NEW_VALUE_LIST: {
      return { ...state, byId: updateAttributeOptions(state, action) };
    }
    default:
      return state;
  }
};

// Selectors
export function getAttribute(state: StoreI, id: string) {
  return state.attributes.byId[id];
}

export function getAttributes({ attributes }: StoreI, internalTypeList?: AttrInternalType[]): AttributeI[] {
  return Object.values(attributes.byId).reduce((acc: AttributeI[], attribute) => {
    if (internalTypeList === undefined || internalTypeList.includes(attribute.internal_type)) {
      acc.push(attribute);
    }
    return acc;
  }, []);
}

// Selectors
export function getAttributeBySlug(state: StoreI, slug: AttrSlugType): AttributeI {
  return getAttributes(state).find((attr: AttributeI) => attr.slug === slug.toString()) as AttributeI;
}

export default attributesReducer;
